import advisorData from "../../app/helpers/advisorData"
import Helpers from "./Helpers"
import type {
  AdvisedProduct,
  AidenRequest,
  AidenResponse,
  Integration,
} from "../../app/types"
import type { Product } from "./types"
import { UsageEvents } from "./UsageEvents"
import type { AdvisorInfo, UsageEvent } from "./UsageEvents/types"

/**
 * Interface which can be used by App.svelte to partially provide methods for events.
 * App.svelte could provide the contextual data to prevent prop-drilling
 * */
export type IntegrationsAnalyticsForComponents = ProductClickedAnalytics &
  CustomerInfoSubmittedAnalytics

export interface ProductClickedAnalytics {
  productClick: (product: Product) => void
}
export interface CustomerInfoSubmittedAnalytics {
  customerInfoSubmitted(info: {
    email: string
    checkboxChecked: boolean
    checkboxText: string
    products: AdvisedProduct[]
  }): void

  infoPageCustomerInfoSubmitted(info: {
    email: string
    checkboxChecked: boolean
    checkboxText: string
    formIdentifier: string
  }): void

  noResultsCustomerInfoSubmitted(info: {
    email: string
    checkboxChecked: boolean
    checkboxText: string
  }): void
}

export type SendIntegrationsAnalytics = ReturnType<typeof IntegrationsAnalytics>

export function IntegrationsAnalytics(
  broadcast: (event: Record<string, unknown>) => void = postParentMessage
) {
  return {
    /** Emit events related to the transition from current state to next state
     * Emits events for:
     * - Answers to Questions
     * - Advice
     **/
    screenChange(
      advisorId: string,
      resp: AidenResponse,
      req: AidenRequest
    ): void {
      // Transitioned to next screen
      if (resp && resp.currentScreenId !== req.screenId) {
        if (req.history.length === 0) {
          handleConversationStarted(advisorId, req, resp).forEach(broadcast)
        }
        if (req.screen.component === "question") {
          handleQuestionAnswered(advisorId, req, resp).forEach(broadcast)
        }
        if (resp.currentScreenId === "ADVICE") {
          try {
            handleAdviceGiven(advisorId, req, resp).forEach(broadcast)
          } catch (e) {
            console.error(e)
          }
        }
      }
    },

    /** Emit events when the advice changes because of a refresh of the advice page
     * Emits events for:
     * - Advice
     **/
    adviceChanged(advisorId: string, resp: AidenResponse): void {
      // Transitioned to next screen

      if (resp.currentScreenId === "ADVICE") {
        try {
          handleAdviceGiven(advisorId, resp, resp).forEach(broadcast)
        } catch (e) {
          console.error(e)
        }
      }
    },

    /**
     * When the user starts over the app.
     */
    startOver(advisorId: string, resp: AidenResponse): void {
      UsageEvents.startOver(
        advisorInfo(advisorId, resp),
        sessionContext(resp),
        resp.integrations
      ).forEach(broadcast)
    },

    /**
     * Form on the advice page to acquire customer information has been submitted.
     */
    customerInfoSubmitted(
      advisorId: string,
      advisorName: string,
      sessionId: string,
      isPreview: boolean,
      integrations: Integration[],
      info: {
        email: string
        checkboxChecked: boolean
        checkboxText: string
        products: AdvisedProduct[]
      }
    ) {
      UsageEvents.customerInfoSubmitted(
        advisorInfo(advisorId, { advisor: { name: advisorName } }),
        sessionContext({ sessionId: sessionId }),
        integrations,
        isPreview,
        {
          email: info.email,
          checkboxChecked: info.checkboxChecked,
          checkboxText: info.checkboxText,
          advisedProducts: info.products,
        }
      ).forEach(broadcast)
    },

    infoPageCustomerInfoSubmitted(
      advisorId: string,
      advisorName: string,
      sessionId: string,
      isPreview: boolean,
      integrations: Integration[],
      info: {
        email: string
        checkboxChecked: boolean
        checkboxText: string
        formIdentifier: string
      }
    ) {
      UsageEvents.infoPageCustomerInfoSubmitted(
        advisorInfo(advisorId, { advisor: { name: advisorName } }),
        sessionContext({ sessionId: sessionId }),
        integrations,
        isPreview,
        {
          email: info.email,
          checkboxChecked: info.checkboxChecked,
          checkboxText: info.checkboxText,
          formIdentifier: info.formIdentifier,
        }
      ).forEach(broadcast)
    },

    noResultsCustomerInfoSubmitted(
      advisorId: string,
      advisorName: string,
      sessionId: string,
      isPreview: boolean,
      integrations: Integration[],
      info: {
        email: string
        checkboxChecked: boolean
        checkboxText: string
      }
    ) {
      UsageEvents.noResultsCustomerInfoSubmitted(
        advisorInfo(advisorId, { advisor: { name: advisorName } }),
        sessionContext({ sessionId: sessionId }),
        integrations,
        isPreview,
        {
          email: info.email,
          checkboxChecked: info.checkboxChecked,
          checkboxText: info.checkboxText,
        }
      ).forEach(broadcast)
    },

    productClick(
      advisorId: string,
      advisorName: string,
      sessionId: string,
      integrations: Integration[],
      product: Product
    ) {
      UsageEvents.clickedOnProduct(
        advisorInfo(advisorId, { advisor: { name: advisorName } }),
        sessionContext({ sessionId: sessionId }),
        integrations,
        product
      ).forEach(broadcast)
    },

    /**
     * Custom action is clicked
     */
    customAction(
      advisorId: string,
      advisorName: string,
      sessionId: string,
      isPreview: boolean,
      integrations: Integration[],
      products: Product[],
      eventIdentifier: string
    ) {
      // Do not emit things to customer analytics yet
      //
      // UsageEvents.customAction(
      //   advisorInfo(advisorId, { advisor: { name: advisorName } }),
      //   sessionContext({ sessionId: sessionId }),
      //   integrations,
      //   isPreview,
      //   products,
      //   eventIdentifier
      // ).forEach(broadcast)
    },
  }
}

const advisorInfo = (
  advisorId: string,
  resp: { advisor: { name: string } }
): AdvisorInfo => ({
  id: advisorId,
  name: resp.advisor.name,
  advisorId: advisorId,
  advisorName: resp.advisor.name,
})

const sessionContext = (obj: { sessionId: string }) => ({
  userId: "TODO",
  sessionId: obj.sessionId,
})

const handleQuestionAnswered = (
  advisorId: string,
  req: AidenRequest,
  resp: AidenResponse
): UsageEvent[] => {
  const question = Helpers.questionFromRequest(req)
  const answers = Helpers.answersFromRequest(req, (attrId) =>
    advisorData.getAttribute(resp, attrId)
  )

  if (question && answers.length !== 0) {
    return UsageEvents.questionAnswered(
      advisorInfo(advisorId, resp),
      sessionContext(req),
      resp.integrations,
      { title: question, label: "TODO" },
      answers
    )
  }
  return []
}

const handleAdviceGiven = (
  advisorId: string,
  req: { sessionId: string },
  resp: AidenResponse
): UsageEvent[] => {
  return UsageEvents.adviceGiven(
    advisorInfo(advisorId, resp),
    sessionContext(req),
    resp.integrations,
    Helpers.advisedProductsFromResponse(resp)
  )
}

const handleConversationStarted = (
  advisorId: string,
  req: { sessionId: string },
  resp: AidenResponse
): UsageEvent[] => {
  return UsageEvents.conversationStarted(
    advisorInfo(advisorId, resp),
    sessionContext(req),
    resp.integrations
  )
}

function postParentMessage(event: Record<string, unknown>) {
  window.parent.postMessage(event, "*")
}

/**
 * Module that knows how to convert and react to screen changes and emit useful analytics
 * to the parent page.
 * */

// const CustomerAnalytics = {
//   /** Emit events related to the transition from current state to next state
//    * Emits events for:
//    * - Answers to Questions
//    * - Advice
//    **/
//   screenChange: (
//     advisorId: string,
//     resp: AidenResponse,
//     req: AidenRequest
//   ): void => {
//     // Transitioned to next screen
//     if (resp && resp.currentScreenId !== req.screenId) {
//       if (req.screen.component === "question") {
//         handleQuestionAnswered(advisorId, req, resp)
//       }

//       if (resp.currentScreenId === "ADVICE") {
//         handleAdviceGiven(advisorId, resp)
//       }
//     }
//   },

//   /** Emit events related to the transition from current state to next state
//    * Emits events for:
//    * - Answers to Questions
//    * - Advice
//    **/
//   startOver: (advisorId: string, resp: AidenResponse): void =>
//     EventEmitter.sendStartOverEvent(
//       advisorId,
//       resp.advisor.name,
//       resp.sessionId,
//       resp.integrations
//     ),

//   /**
//    * CustomerInfoSubmitted
//    */
//   customerInfoSubmitted(
//     advisorId: string,
//     advisorName: string,
//     isPreview: boolean,
//     integrations: Integration[],
//     info: {
//       email: string
//       checkboxChecked: boolean
//       products: ProductControl[]
//     }
//   ) {
//     EventEmitter.customerInfoSubmitted(
//       advisorId,
//       advisorName,
//       isPreview,
//       integrations,
//       {
//         email: info.email,
//         checkboxChecked: info.checkboxChecked,
//         advisedProducts: Helpers.productControlsToAdvisedProducts(
//           info.products
//         ),
//       }
//     )
//   },

//   productClick(advisorId: string, advisorName: string, product: Product) {
//     EventEmitter.sendProductClickEvent(advisorId, advisorName, product)
//   },
// }

// const handleQuestionAnswered = (
//   advisorId: string,
//   req: AidenRequest,
//   resp: AidenResponse
// ) => {
//   const question = Helpers.questionFromRequest(req)
//   const answers = Helpers.answersFromRequest(req, (attrId) =>
//     advisorData.getAttribute(resp, attrId)
//   )

//   if (question && answers.length !== 0) {
//     EventEmitter.sendAnsweredEvent(
//       advisorId,
//       resp.advisor.name,
//       req.sessionId,
//       question,
//       answers,
//       resp.integrations
//     )
//   }
// }

// const handleAdviceGiven = (advisorId: string, resp: AidenResponse) => {
//   EventEmitter.sendAdviceEvent(
//     advisorId,
//     resp.advisor.name,
//     resp.sessionId,
//     Helpers.productsFromResponse(resp),
//     resp.integrations
//   )
// }
